// MACHINERY V2 (Stroje) — profile dialog state (M2.4 logic, extracted MDEL-FE).
//
// Split out of useProfileActions.ts, which was 253 lines and inside the 300-line
// cap's danger zone before MDEL-FE added the hard-delete action to it. Same
// split, same reason and same shape as the fleet's: fleet/useFleetDialogs.ts
// owns which fleet dialog is open, this owns which PROFILE dialog is open, and
// the actions hook next door is left with the action set and its handlers.
//
// Nothing here is new logic — it is M2.4's `useState` block and its
// `dialogProps` literal moved verbatim, including the report-problem success
// toast and the "every profile mutation refetches THIS machine" rule, which is
// the `refresh` callback the caller passes in (the profile's whole reason for
// not reusing useFleetHandlers).
//
// The dialogs themselves are the EXISTING (MUI) ones, mounted by ProfileDialogs
// per ticket §5 — this hook only tracks which is open.
//
// THE RETURNED OBJECT IS MEMOISED, AND THAT IS PART OF "NOTHING NEW" (MDEL-FE
// review finding 7). The first cut returned a fresh literal every render, so
// `dialogs` changed identity every render and useProfileActions' `fullActions`
// / `actions` / `findAction` and its returned callbacks were all rebuilt each
// time — harmless today, since no consumer is memoised, but a real behavioural
// delta inside a change documented as a pure move. Before the split these were
// plain useState values in the actions hook and were stable between renders;
// the useMemo restores exactly that.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useMemo, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { setSuccess } from "../../customerMachinerySlice";
import { ProfileDialogsProps } from "./ProfileDialogs";

export interface ProfileDialogControls {
  /** Everything ProfileDialogs needs except the machine itself. */
  dialogProps: Omit<ProfileDialogsProps, "machine">;
  openQr: () => void;
  openEdit: () => void;
  openAssignGroup: () => void;
  openDecommission: () => void;
  openReportProblem: () => void;
}

export const useProfileDialogs = (refresh: () => void): ProfileDialogControls => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");

  const [qrOpen, setQrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [assignGroupOpen, setAssignGroupOpen] = useState(false);
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [reportProblemOpen, setReportProblemOpen] = useState(false);

  const openQr = useCallback(() => setQrOpen(true), []);
  const openEdit = useCallback(() => setEditOpen(true), []);
  const openAssignGroup = useCallback(() => setAssignGroupOpen(true), []);
  const openDecommission = useCallback(() => setDecommissionOpen(true), []);
  const openReportProblem = useCallback(() => setReportProblemOpen(true), []);

  const dialogProps: Omit<ProfileDialogsProps, "machine"> = {
    qrOpen,
    onQrClose: () => setQrOpen(false),
    editOpen,
    onEditClose: () => setEditOpen(false),
    onEditSaved: () => {
      setEditOpen(false);
      refresh();
    },
    assignGroupOpen,
    onAssignGroupClose: () => setAssignGroupOpen(false),
    onAssigned: () => {
      setAssignGroupOpen(false);
      refresh();
    },
    decommissionOpen,
    onDecommissionClose: () => setDecommissionOpen(false),
    onDecommissioned: () => {
      setDecommissionOpen(false);
      refresh();
    },
    reportProblemOpen,
    onReportProblemClose: () => setReportProblemOpen(false),
    onReportProblemSuccess: (srNumber: string) => {
      dispatch(
        setSuccess(translate("myMachinery:reportProblem.successDetail", { number: srNumber }))
      );
      refresh();
    },
  };

  // `dialogProps` is rebuilt every render by design (it closes over the five
  // open flags), so the identity of THIS object can only be stable for the
  // openers — which is what the actions hook depends on. It is listed as a
  // dep of nothing that re-fetches; see the header.
  return useMemo(
    () => ({
      dialogProps,
      openQr,
      openEdit,
      openAssignGroup,
      openDecommission,
      openReportProblem,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      qrOpen,
      editOpen,
      assignGroupOpen,
      decommissionOpen,
      reportProblemOpen,
      openQr,
      openEdit,
      openAssignGroup,
      openDecommission,
      openReportProblem,
    ]
  );
};

export default useProfileDialogs;
